/**
 * Glossary of knowledge-points that may appear in weekly learning reports.
 * Keyed by the canonical English term as used in Gemini output.
 *
 * - `pattern`: Regex fragment matched case-insensitively inside the report
 *   body. Use word boundaries for ASCII; for multi-word phrases include them.
 * - `title`: Shown as the modal heading.
 * - `summary`: 1–2 sentence plain explanation shown inside the modal.
 * - `href`: Destination of the "了解更多" button (opens in new tab).
 *
 * The glossary is deliberately small; extend it as Gemini learns new terms.
 */

export interface KnowledgeGlossaryEntry {
    /** Unique key used for React lists + matched against display text. */
    key: string;
    /** The canonical term rendered on the pill. */
    term: string;
    /** Case-insensitive regex fragment used to detect the term in body text. */
    pattern: string;
    /** Modal heading (supports simple bilingual form). */
    title: string;
    /** Short explanation shown inside the modal. */
    summary: string;
    /** Target page opened by the "了解更多" button (new tab). */
    href: string;
}

/**
 * Glossary contains both:
 *   (A) English linguistics terms the AI prompt keeps in English (Morphology,
 *       Prosody, Weak Form, Shadowing, Fluency, Phonics, FSRS, CEFR codes).
 *   (B) Native-language platform/pedagogy terms the AI prompt translates
 *       (記憶健康度, 需複習, 穩固, 衰退中, 失效次數, 留存率, 字首, 字尾, 字根, ...).
 * Both render as clickable pills with a modal + "深入了解" link. The native
 * entries below are zh-TW; sister locales (zh-CN, ja, ko, vi, th, id, ms)
 * can be added as we verify their AI output.
 */
export const KNOWLEDGE_GLOSSARY: KnowledgeGlossaryEntry[] = [
    {
        key: 'fsrs',
        term: 'FSRS',
        pattern: '\\bFSRS\\b',
        title: 'FSRS 演算法',
        summary:
            'Free Spaced Repetition Scheduler，根據你每次複習的難度，精準計算下一次該看到這個單字的時間，讓記憶留存率最大化。',
        href: '/knowledge-graph#fsrs',
    },
    {
        key: 'morphology',
        term: 'Morphology',
        pattern: '\\bMorphology\\b',
        title: 'Morphology（構詞法）',
        summary:
            '研究單字如何由字根、字首、字尾組成。掌握 Morphology 能讓你從未知單字的零件推敲意義，大幅減輕記憶負擔。',
        href: '/knowledge-graph#morphology',
    },
    {
        key: 'prosody',
        term: 'Prosody',
        pattern: '\\bProsody\\b',
        title: 'Prosody（韻律感）',
        summary:
            '句子的重音、節奏、語調。母語人士聽起來流暢的關鍵，不在於單字發音是否準確，而在於 Prosody 是否自然。',
        href: '/knowledge-graph#prosody',
    },
    {
        key: 'weak-form',
        term: 'Weak Form',
        pattern: '\\bWeak\\s+Form\\b',
        title: 'Weak Form（弱讀）',
        summary:
            '功能詞（as、of、and、to 等）在自然口說中會弱讀成 /ə/。掌握弱讀能讓句子連貫、聽起來更接近母語。',
        href: '/knowledge-graph#prosody',
    },
    {
        key: 'shadowing',
        term: 'Shadowing',
        pattern: '\\bShadowing\\b',
        title: 'Shadowing（跟讀）',
        summary:
            '跟著母語錄音同步模仿念出。每天 5 分鐘 Shadowing 可顯著提升口語流暢度與聽力理解。',
        href: '/knowledge-graph#shadowing',
    },
    {
        key: 'fluency',
        term: 'Fluency',
        pattern: '\\bFluency\\b',
        title: 'Fluency（流暢度）',
        summary:
            '口語表達的速度與連貫性。和 accuracy（準確度）不同，Fluency 著重「不卡」，即使有小錯也不影響溝通。',
        href: '/knowledge-graph#speaking',
    },
    {
        key: 'phonics',
        term: 'Phonics',
        pattern: '\\bPhonics\\b',
        title: 'Phonics（自然發音）',
        summary:
            '把字母與發音對應起來的系統，例如 "sh" 發 /ʃ/、"igh" 發 /aɪ/。掌握 Phonics 可以讓你看見新字就念得出來。',
        href: '/knowledge-graph#phonics',
    },
    {
        key: 'cefr-c1',
        term: 'C1',
        pattern: '\\bC1\\b',
        title: 'CEFR C1 等級',
        summary:
            '歐洲共同語言參考標準的「流利運用」等級。能理解廣泛主題並表達複雜觀點，是進階學習者的重要分水嶺。',
        href: '/cefr-guide',
    },
    {
        key: 'cefr-c2',
        term: 'C2',
        pattern: '\\bC2\\b',
        title: 'CEFR C2 等級',
        summary:
            '歐洲共同語言參考標準的最高等級「精通」。接近母語人士水準，能處理任何聽讀內容並流暢表達細微差異。',
        href: '/cefr-guide',
    },

    // --- Native-language (zh-TW) platform/pedagogy terms ---
    // Morphology sub-parts
    {
        key: 'prefix-zh',
        term: '字首',
        pattern: '字首',
        title: '字首（Prefix）',
        summary:
            '加在字根前面、改變單字意義的成分，例如 im- 表示「不/相反」(im-possible)。掌握常見字首能讓你從零件推敲新字含義。',
        href: '/knowledge-graph#morphology',
    },
    {
        key: 'suffix-zh',
        term: '字尾',
        pattern: '字尾',
        title: '字尾（Suffix）',
        summary:
            '加在字根後面、改變單字詞性或細微意義的成分，例如 -ous 表示「具有…特性」(fam-ous)。觀察字尾能快速判斷單字是名詞、形容詞或副詞。',
        href: '/knowledge-graph#morphology',
    },
    {
        key: 'root-zh',
        term: '字根',
        pattern: '字根',
        title: '字根（Root）',
        summary:
            '單字的核心意義來源，例如 fic 來自拉丁文「做」(bene-fic-ial、of-fic-e)。同一字根衍生出的家族單字通常語意相關。',
        href: '/knowledge-graph#morphology',
    },
    // FSRS / memory-health family
    {
        key: 'memory-health-zh',
        term: '記憶健康度',
        pattern: '記憶健康度',
        title: '記憶健康度',
        summary:
            'FSRS 演算法對你整體詞彙記憶狀態的總體評估，由「穩固／需複習／衰退中」三個桶的比例組成。比例越偏向穩固，代表你的詞彙基底越紮實。',
        href: '/knowledge-graph#fsrs',
    },
    {
        key: 'need-review-zh',
        term: '需複習',
        pattern: '需複習',
        title: '需複習',
        summary:
            '根據 FSRS 推算，這些單字此刻正處於即將遺忘的邊緣。優先清掉「需複習」清單，記憶留存率會明顯上升。',
        href: '/knowledge-graph#fsrs',
    },
    {
        key: 'solid-zh',
        term: '穩固',
        pattern: '穩固',
        title: '穩固',
        summary:
            '記憶強度高、短期內不太會忘記的單字。穩固比例越高，代表你的複習節奏跟 FSRS 時間軸對齊得越好。',
        href: '/knowledge-graph#fsrs',
    },
    {
        key: 'fading-zh',
        term: '衰退中',
        pattern: '衰退中',
        title: '衰退中',
        summary:
            '記憶已開始衰減、預測在幾天內就會遺忘的單字。若不盡快複習，就會從「衰退中」跌入失效狀態、變回生字。',
        href: '/knowledge-graph#fsrs',
    },
    {
        key: 'lapses-zh',
        term: '失效次數',
        pattern: '失效次數',
        title: '失效次數（Lapses）',
        summary:
            '一個單字被標記為「忘記」的累積次數。失效次數偏高的單字代表你的記憶抓不住它，通常需要搭配詞根、情境、例句一起處理。',
        href: '/knowledge-graph#fsrs',
    },
    {
        key: 'retention-zh',
        term: '留存率',
        pattern: '留存率',
        title: '留存率（Retention）',
        summary:
            '過去一段時間內，你複習成功（記得）的單字比例。FSRS 會把你的目標留存率校準在約 90%——太高代表過度複習、太低代表節奏沒跟上。',
        href: '/knowledge-graph#fsrs',
    },
    {
        key: 'streak-zh',
        term: '連續天數',
        pattern: '連續天數',
        title: '連續天數（Streak）',
        summary:
            '連續有學習紀錄的天數。間隔重複需要規律節奏——連續天數穩定比單日長時間學習對長期記憶效果好很多。',
        href: '/knowledge-graph#fsrs',
    },
    {
        key: 'due-today-zh',
        term: '今日到期',
        pattern: '今日到期',
        title: '今日到期（Due Today）',
        summary:
            'FSRS 判斷今天正好到了該複習的單字數量。把當日到期清掉就像刷牙，讓記憶曲線保持在穩定區間。',
        href: '/knowledge-graph#fsrs',
    },
];

/** Pre-compiled combined regex used by the renderer. Capturing group = matched term. */
export const KNOWLEDGE_GLOSSARY_REGEX = new RegExp(
    `(${KNOWLEDGE_GLOSSARY.map((e) => e.pattern).join('|')})`,
    'gi',
);

/** Look up which glossary entry a matched string belongs to. */
export function findGlossaryEntry(matched: string): KnowledgeGlossaryEntry | null {
    const normalized = matched.trim();
    for (const entry of KNOWLEDGE_GLOSSARY) {
        if (new RegExp(`^${entry.pattern}$`, 'i').test(normalized)) {
            return entry;
        }
    }
    return null;
}
